import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Link2 } from "lucide-react";
import { useApartments } from "../hooks/useApartments";
import { AdminSmoobuIcal } from "./AdminSmoobuIcal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSmoobu = ({ headers }) => {
  const APARTMENTS = useApartments();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ api_key: "", api_secret: "", channel_id: 70 });
  const [smoobuApartments, setSmoobuApartments] = useState([]);
  const [mapping, setMapping] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const inputCls =
    "w-full bg-transparent border-b border-[#C29B62]/40 focus:border-[#1C3829] outline-none py-3 text-base font-light placeholder:text-[#1A1A1A]/35 transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 mb-1";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/smoobu`, { headers: headers() });
      setStatus(res.data);
      setMapping(res.data.mapping || {});
      if (res.data.channel_id) setForm((f) => ({ ...f, channel_id: res.data.channel_id }));
    } catch {
      toast.error("Errore nel caricamento delle impostazioni Smoobu");
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const saveCredentials = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/smoobu/settings`,
        {
          api_key: form.api_key || undefined,
          api_secret: form.api_secret || undefined,
          channel_id: Number(form.channel_id) || undefined,
        },
        { headers: headers() }
      );
      toast.success("Credenziali salvate");
      setForm((f) => ({ ...f, api_key: "", api_secret: "" }));
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const loadApartments = async () => {
    try {
      const res = await axios.get(`${API}/admin/smoobu/apartments`, { headers: headers() });
      setSmoobuApartments(res.data);
      if (res.data.length === 0) toast.error("Nessun appartamento trovato su Smoobu");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore di connessione a Smoobu");
    }
  };

  const saveMapping = async () => {
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      await axios.post(`${API}/admin/smoobu/mapping`, { mapping: clean }, { headers: headers() });
      toast.success("Mappatura salvata");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/smoobu/sync`, {}, { headers: headers() });
      toast.success(`Sincronizzazione completata: ${res.data.synced} prenotazioni importate${res.data.skipped ? `, ${res.data.skipped} ignorate` : ""}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore di sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  if (!status) return null;

  return (
    <section data-testid="admin-smoobu-section" className="border border-[#C29B62]/25 bg-white/60 p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link2 className="w-4 h-4 text-[#C29B62]" />
          <h2 className="text-2xl font-light text-[#1C3829] font-serif-display">Channel manager Smoobu</h2>
        </div>
        <span
          data-testid="smoobu-status-badge"
          className={`text-xs uppercase tracking-[0.2em] px-3 py-1 border ${
            status.configured ? "border-[#1C3829]/30 text-[#1C3829]" : "border-[#C29B62]/40 text-[#8a6a3a]"
          }`}
        >
          {status.configured ? `Connesso · chiave ${status.masked_key}` : "Non configurato"}
        </span>
      </div>
      <p className="mt-2 text-xs text-[#1A1A1A]/50">
        Le prenotazioni ricevute su Smoobu (Booking, Airbnb, ecc.) bloccano automaticamente le date sul sito.
        La sincronizzazione parte da sola ogni 3 ore per tutti gli appartamenti collegati; puoi forzarla con «Sincronizza ora».
      </p>

      <AdminSmoobuIcal headers={headers} />

      <div className="mt-10 border-t border-[#C29B62]/20 pt-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50">
          Connessione API avanzata (facoltativa) — Trovi la chiave API in Smoobu: Impostazioni → Avanzate → Chiavi API.
        </p>
      </div>

      <form onSubmit={saveCredentials} className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6 items-end" data-testid="smoobu-credentials-form">
        <div>
          <label className={labelCls}>API Key</label>
          <input data-testid="smoobu-api-key" className={inputCls} placeholder={status.configured ? status.masked_key : ""} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>API Secret</label>
          <input data-testid="smoobu-api-secret" type="password" className={inputCls} placeholder={status.configured ? "••••••••" : ""} value={form.api_secret} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Channel ID</label>
          <input data-testid="smoobu-channel-id" type="number" className={inputCls} value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} />
        </div>
        <button
          data-testid="smoobu-save-button"
          type="submit"
          disabled={saving || (!form.api_key && !form.api_secret)}
          className="bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:text-[#C29B62] transition-colors disabled:opacity-40"
        >
          Salva
        </button>
      </form>

      {status.configured && (
        <div className="mt-10 border-t border-[#C29B62]/20 pt-8 space-y-8">
          <div>
            <div className="flex flex-wrap items-center gap-4">
              <p className={labelCls}>Collega gli appartamenti</p>
              <button type="button" data-testid="smoobu-load-apartments" onClick={loadApartments} className="text-xs uppercase tracking-[0.2em] text-[#1C3829] underline underline-offset-4 hover:text-[#C29B62] transition-colors">
                Carica appartamenti da Smoobu
              </button>
            </div>
            {smoobuApartments.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="smoobu-mapping-grid">
                {APARTMENTS.map((a) => (
                  <div key={a.id}>
                    <label className={labelCls}>{a.name}</label>
                    <select
                      data-testid={`smoobu-map-${a.id}`}
                      className={`${inputCls} bg-transparent`}
                      value={mapping[a.id] || ""}
                      onChange={(e) => setMapping({ ...mapping, [a.id]: e.target.value ? Number(e.target.value) : "" })}
                    >
                      <option value="">— non collegato —</option>
                      {smoobuApartments.map((sa) => (
                        <option key={sa.id} value={sa.id}>{sa.name} (ID {sa.id})</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
            {smoobuApartments.length > 0 && (
              <button type="button" data-testid="smoobu-save-mapping" onClick={saveMapping} className="mt-6 bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:text-[#C29B62] transition-colors">
                Salva collegamenti
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <button
              type="button"
              data-testid="smoobu-sync-button"
              onClick={syncNow}
              disabled={syncing}
              className="flex items-center gap-2 bg-[#C29B62] text-[#101f17] text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:bg-[#1C3829] hover:text-[#F7F5F0] transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizzazione…" : "Sincronizza ora"}
            </button>
            {status.last_sync && (
              <p className="text-xs text-[#1A1A1A]/50" data-testid="smoobu-last-sync">
                Ultima sincronizzazione: {new Date(status.last_sync).toLocaleString("it-IT")}
              </p>
            )}
          </div>

          <div className="text-xs text-[#1A1A1A]/50 space-y-1" data-testid="smoobu-webhook-info">
            <p className="uppercase tracking-[0.2em]">Webhook (facoltativo, per sincronizzazione immediata)</p>
            <p>URL: <code className="text-[#1C3829]">{`${process.env.REACT_APP_BACKEND_URL}/api/smoobu/webhook`}</code></p>
            <p>Header <code className="text-[#1C3829]">X-Webhook-Token</code>: <code className="text-[#1C3829]">{status.webhook_token}</code></p>
          </div>
        </div>
      )}
    </section>
  );
};
